import { AdminMessage } from "@components/admin/admin-helpers"
import { ResponsiveImage } from "@components/atoms/ResponsiveImage"
import { Chip } from "@components/ui/Chip"
import { GuestRowCardFragment } from "@generated-types"
import { formatLongDateTime, formatNiceDate, formatNiceTime } from "@utils/date-format"
import { tv } from "@utils/tw"
import { defineBlock, EditableText } from "eddev/blocks"
import { useMemo } from "react"

export const meta: BlockMeta = {
  title: "Film Guests",
  icon: "admin-users",
  tags: ["film-text"],
  description: "Displays guests attached to a film",
  category: "embed",
  conditions: {
    multiple: false,
  },
}

type GuestEntry = GuestRowCardFragment & {
  sessions: {
    id: string
    venueName: string
    dateTime: string
  }[]
}

export default defineBlock("film/film-guests", (props) => {
  const guests = useMemo(() => {
    const guests = new Map<string, GuestEntry>()

    if (props.film?.sessions) {
      for (const session of props.film?.sessions) {
        for (const guest of session?.guests ?? []) {
          if (!guests.has(guest?.title!)) {
            guests.set(guest?.title!, {
              ...guest,
              sessions: [],
            } as GuestEntry)
          }
          guests.get(guest?.title!)?.sessions.push({
            id: session?.id!,
            venueName: session?.info?.venue?.titleSimple!,
            dateTime: session?.info?.dateTime!,
          })
        }
      }
    }

    for (const guest of guests.values()) {
      guest.sessions.sort((a, b) => a.dateTime.localeCompare(b.dateTime))
    }

    return [...guests.values()].sort((a, b) => {
      return a.title?.localeCompare(b.title!)! || 0
    })
  }, [props.film?.sessions])

  const allGuestsAtAllSessions = guests.every(
    (guest) => guest.sessions.length === props.film?.sessions?.filter(Boolean).length,
  )

  if (!guests?.length) {
    if (env.admin) {
      return <AdminMessage className="my-3">No guests to show</AdminMessage>
    }
    return null
  }

  return (
    <div className="my-10">
      <EditableText as="h4" plainText store="heading" defaultValue="Special Guests" className="type-label-caps mb-5" />
      <div className="relative flex flex-col gap-8 md:gap-5">
        {guests.map((guest, index) => {
          return <GuestRow {...guest!} showSessions={!allGuestsAtAllSessions} key={index} />
        })}
      </div>
    </div>
  )
})

function GuestRow(props: GuestEntry & { showSessions?: boolean }) {
  const styles = guestStyles()
  return (
    <div className={styles.base()}>
      {props?.guestInfo?.headshot && (
        <div className={styles.image()}>
          <ResponsiveImage {...props.guestInfo.headshot} aspect={1} />
        </div>
      )}
      <div className={styles.info()}>
        <div className={styles.heading()}>
          <div className={styles.name()}>{props.title}</div>
          {!!props.guestInfo?.role && (
            <Chip shape="rounded" variant="high">
              {props.guestInfo.role}
            </Chip>
          )}
        </div>
        <div className={styles.bio()}>
          <p dangerouslySetInnerHTML={{ __html: props.guestInfo?.bio! }} />
        </div>
        {props.showSessions && props.sessions.length > 0 && (
          <div className="flex flex-wrap gap-2">
            <div className="type-label-s">Appearing at:</div>
            {props.sessions.map((session) => (
              <Chip key={session.id} variant="mid" shape="rounded">
                {session.venueName} - {formatNiceDate(session.dateTime)}
                {", "}
                {formatNiceTime(session.dateTime)}
              </Chip>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

const guestStyles = tv({
  base: "flex flex-wrap gap-x-4 gap-y-3 md:flex-nowrap",
  slots: {
    image: "flex-none w-9 md:w-[100px]",
    heading: "flex gap-3 flex-wrap items-center flex-1",
    name: "type-label-m",
    bio: "type-body-s w-full text-fg-secondary [&>p]:max-w-[45em] text-pretty",
    info: "contents md:flex md:gap-2 md:flex-col",
  },
})
